import React from 'react';
import { Tabs, TabsList, TabsTrigger } from '../ui/Tabs';
import { Button } from '../ui/Button';
import { Building2, Clock, CheckCircle, UserCheck } from 'lucide-react';
import { ProfileStatusFilter, ProfilesCounts } from '../../types';

interface ProfilesFiltersProps {
  activeTab: ProfileStatusFilter;
  tierFilter: string;
  billingFilter: string;
  counts: ProfilesCounts;
  onTabChange: (tab: ProfileStatusFilter) => void;
  onTierFilterChange: (tier: string) => void;
  onBillingFilterChange: (billing: string) => void;
  onClearFilters: () => void;
  className?: string;
}

export const ProfilesFilters: React.FC<ProfilesFiltersProps> = ({
  activeTab,
  tierFilter,
  billingFilter,
  counts,
  onTabChange,
  onTierFilterChange,
  onBillingFilterChange,
  onClearFilters,
  className
}) => {
  return (
    <div className={className}>
      {/* Status Filter Tabs */}
      <div className="mb-6">
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList className="grid w-full grid-cols-4 lg:w-fit">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              All ({counts.total})
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending ({counts.pending})
            </TabsTrigger>
            <TabsTrigger value="processed" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Processed ({counts.processed})
            </TabsTrigger>
            <TabsTrigger value="assigned" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Assigned ({counts.assigned})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Service Filters */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filter by:</span>
          </div>
          
          {/* Tier Level Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Service Tier:</span>
            <select 
              value={tierFilter}
              onChange={(e) => onTierFilterChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Tiers</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          {/* Billing Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Billing Type:</span>
            <select 
              value={billingFilter}
              onChange={(e) => onBillingFilterChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="one-time">One-time</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(tierFilter !== 'all' || billingFilter !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              className="text-xs"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};